import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Clock, MapPin, CheckCircle, XCircle, Calendar, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, differenceInHours } from 'date-fns';

import { trainingAPI } from '../services/api';

const TrainingAttendance = ({ refreshKey }) => {
    const { user } = useAuth();
    const [upcomingTrainings, setUpcomingTrainings] = useState([]);
    const [stats, setStats] = useState({ total_trainings: 0, attended: 0, attendance_rate: 0 });
    const [loading, setLoading] = useState(true);
    const [rsvpLoading, setRsvpLoading] = useState({});

    useEffect(() => {
        loadData();
    }, [refreshKey]);

    const loadData = async () => {
        try {
            setLoading(true);

            // Load upcoming trainings
            const upcomingRes = await trainingAPI.getUpcoming();
            const upcomingData = upcomingRes.data;

            // Load user's attendance records
            const attendanceRes = await trainingAPI.getMyAttendance();
            const attendanceData = attendanceRes.data;

            // Load stats
            const statsRes = await trainingAPI.getAttendanceStats();
            const statsData = statsRes.data;

            // Merge attendance status with upcoming trainings
            const trainingsWithStatus = upcomingData.map(training => {
                const record = attendanceData.find(a => a.event_id === training.id);
                return {
                    ...training,
                    userStatus: record?.status,
                    attendanceRecord: record
                };
            });

            // Sort by start_datetime
            trainingsWithStatus.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));

            setUpcomingTrainings(trainingsWithStatus);
            setStats(statsData);
        } catch (error) {
            console.error('Failed to load training data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRSVP = async (eventId, status) => {
        try {
            setRsvpLoading(prev => ({ ...prev, [eventId]: true }));

            await trainingAPI.submitAttendance({ event_id: eventId, status });

            await loadData();
        } catch (error) {
            console.error('RSVP error:', error);
            alert('Failed to submit RSVP');
        } finally {
            setRsvpLoading(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const canRSVP = (training) => {
        const trainingDate = parseISO(training.start_datetime);
        const now = new Date();
        const hoursUntil = differenceInHours(trainingDate, now);

        // Allow RSVP for any future event (and events that started within last 2 hours)
        return hoursUntil > -2;
    };

    if (loading) {
        return (
            <Card className="bg-[#1a1a1b] border-gray-800">
                <CardContent className="p-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-[#1a1a1b] border-gray-800">
            <CardHeader className="border-b border-gray-800">
                <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    Antrenman Katılımı
                </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
                {/* Attendance Stats */}
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg border border-blue-500/30">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                            <span className="text-sm md:text-base text-gray-300 font-medium">Son 30 Gün</span>
                        </div>
                        <span className="text-xl md:text-2xl font-bold text-white">{stats.attendance_rate}%</span>
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 mb-2">
                        {stats.attended} / {stats.total_trainings} idmana katıldınız
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                            style={{ width: `${stats.attendance_rate}%` }}
                        />
                    </div>
                </div>

                {/* Upcoming Trainings */}
                <div>
                    <h3 className="text-sm md:text-base text-gray-300 font-medium mb-3">Sıradaki İdman</h3>
                    {upcomingTrainings.length === 0 ? (
                        <p className="text-center text-gray-500 py-6 md:py-8 text-sm">Yaklaşan idman bulunmuyor</p>
                    ) : (
                        <div className="space-y-3">
                            {upcomingTrainings.slice(0, 1).map(training => {
                                const rsvpEnabled = canRSVP(training);
                                const isLoading = rsvpLoading[training.id];

                                return (
                                    <div
                                        key={training.id}
                                        className="p-3 md:p-4 bg-[#0a0a0b] rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm md:text-base text-white font-medium mb-1 truncate">{training.title}</h4>
                                                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 text-xs md:text-sm text-gray-400">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
                                                        {format(parseISO(training.start_datetime), 'dd MMM, HH:mm')}
                                                    </div>
                                                    {training.location && (
                                                        <div className="flex items-center gap-1">
                                                            <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
                                                            <span className="truncate">{training.location}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* RSVP Status/Buttons */}
                                            {rsvpEnabled ? (
                                                <div className="flex flex-col items-end gap-1.5">
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleRSVP(training.id, 'attending')}
                                                            disabled={isLoading}
                                                            title="Katılacağım"
                                                            className={`
                                                                relative h-10 w-10 md:h-9 md:w-9 p-0 font-medium rounded-lg transition-all duration-300 overflow-hidden group
                                                                ${training.userStatus === 'attending'
                                                                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/30 border-2 border-green-500/50'
                                                                    : 'bg-gray-800/50 border-2 border-gray-700 text-gray-400 hover:border-green-500/50 hover:bg-green-600/10 hover:text-green-400'
                                                                }
                                                            `}
                                                        >
                                                            {training.userStatus === 'attending' && (
                                                                <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 animate-pulse"></div>
                                                            )}
                                                            <CheckCircle className={`w-5 h-5 relative z-10 ${training.userStatus === 'attending' ? 'drop-shadow-sm' : ''}`} />
                                                        </Button>

                                                        {/* Katılamam Button */}
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleRSVP(training.id, 'not_attending')}
                                                            disabled={isLoading}
                                                            title="Katılamam"
                                                            className={`
                                                                relative h-10 w-10 md:h-9 md:w-9 p-0 font-medium rounded-lg transition-all duration-300 overflow-hidden group
                                                                ${training.userStatus === 'not_attending'
                                                                    ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg shadow-red-500/30 border-2 border-red-500/50'
                                                                    : 'bg-gray-800/50 border-2 border-gray-700 text-gray-400 hover:border-red-500/50 hover:bg-red-600/10 hover:text-red-400'
                                                                }
                                                            `}
                                                        >
                                                            {training.userStatus === 'not_attending' && (
                                                                <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-rose-400/20 animate-pulse"></div>
                                                            )}
                                                            <XCircle className={`w-5 h-5 relative z-10 ${training.userStatus === 'not_attending' ? 'drop-shadow-sm' : ''}`} />
                                                        </Button>
                                                    </div>

                                                    {/* Edited Indicator */}
                                                    {training.attendanceRecord &&
                                                        new Date(training.attendanceRecord.updated_at).getTime() > new Date(training.attendanceRecord.created_at).getTime() + 1000 && (
                                                            <div className="text-[10px] text-gray-500 flex items-center gap-1 italic">
                                                                <span>düzenlendi</span>
                                                            </div>
                                                        )}
                                                </div>
                                            ) : training.userStatus && (
                                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${training.userStatus === 'attending'
                                                    ? 'bg-gradient-to-r from-green-600/20 to-emerald-600/20 text-green-400 border border-green-500/40 shadow-sm shadow-green-500/10'
                                                    : 'bg-gradient-to-r from-red-600/20 to-rose-600/20 text-red-400 border border-red-500/40 shadow-sm shadow-red-500/10'
                                                    }`}
                                                    title={training.userStatus === 'attending' ? 'Katılacağım' : 'Katılamam'}
                                                >
                                                    {training.userStatus === 'attending' ? (
                                                        <CheckCircle className="w-5 h-5" />
                                                    ) : (
                                                        <XCircle className="w-5 h-5" />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {!rsvpEnabled && !training.userStatus && (
                                            <p className="text-xs text-red-500 mt-2">
                                                Bu etkinlik için katılım süresi doldu
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default TrainingAttendance;
